import { useMemo } from "react";
import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { useNavigate } from "@tanstack/react-router";
import {
	BookOpen,
	Brain,
	Building2,
	ChartColumn,
	CreditCard,
	Home,
	KeyRound,
	Plug,
	Settings,
	Webhook,
} from "lucide-react";
import { useLocale, useTranslations } from "use-intl";

import { SearchBar, useSiteSearch, type SearchEntry } from "@lawline/ui/search";

import { useAuth } from "@platform/auth/context";
import type { PermissionRequest } from "@platform/auth/guards";
import { docsLink } from "@platform/core/docs";

//
// Pages
//

// `to` is constrained to the known route paths so navigation stays type-checked
// by the router.
type SearchRoute =
	| "/"
	| "/api-keys"
	| "/ai-apps"
	| "/webhooks"
	| "/memory"
	| "/usage"
	| "/tenant"
	| "/billing"
	| "/settings";

// Stable id; maps to common.search.pages.<id> for the translated label and
// description (constants cannot call hooks).
type SearchPageId =
	| "home"
	| "apiKeys"
	| "aiApps"
	| "webhooks"
	| "memory"
	| "usage"
	| "tenant"
	| "billing"
	| "settings";

type PageEntry = SearchEntry & {
	id: SearchPageId;
	to: SearchRoute;
	// Hidden from results unless the current member holds this permission.
	permission?: PermissionRequest;
};

// The searchable surface of the app: every navigable page, with synonyms a user
// might type. Keep in sync with the router's route tree.
const SEARCH_PAGES: readonly PageEntry[] = [
	{
		id: "home",
		to: "/",
		label: "Home",
		description: "Dashboard overview",
		keywords: ["dashboard", "start", "overview"],
		icon: Home,
	},
	{
		id: "apiKeys",
		to: "/api-keys",
		label: "API keys",
		description: "Create and manage API keys",
		keywords: ["api", "keys", "tokens", "secrets", "credentials"],
		icon: KeyRound,
		permission: { apiKey: ["read"] },
	},
	{
		id: "aiApps",
		to: "/ai-apps",
		label: "Connect AI apps",
		description: "Connect Claude, ChatGPT, Cursor or Claude Code with sign-in",
		keywords: ["ai", "apps", "claude", "chatgpt", "cursor", "code", "mcp", "oauth", "connector"],
		icon: Plug,
	},
	{
		id: "webhooks",
		to: "/webhooks",
		label: "Webhooks",
		description: "Endpoints, signing secrets and the delivery log",
		keywords: ["webhooks", "events", "endpoint", "callback", "signing", "secret", "deliveries"],
		icon: Webhook,
		permission: { webhook: ["read"] },
	},
	{
		id: "memory",
		to: "/memory",
		label: "Memory",
		description: "Agent context and memory files",
		keywords: ["memory", "context", "instructions", "prompt", "files"],
		icon: Brain,
		permission: { memory: ["read"] },
	},
	{
		id: "usage",
		to: "/usage",
		label: "Usage",
		description: "Request and token usage",
		keywords: ["usage", "metrics", "stats", "tokens", "requests"],
		icon: ChartColumn,
		permission: { usage: ["read"] },
	},
	{
		id: "tenant",
		to: "/tenant",
		label: "Tenant",
		description: "Members and tenant settings",
		keywords: ["tenant", "organization", "org", "members", "team"],
		icon: Building2,
	},
	{
		id: "billing",
		to: "/billing",
		label: "Billing",
		description: "Plan, invoices, and payment",
		keywords: ["billing", "invoice", "payment", "plan", "subscription"],
		icon: CreditCard,
		permission: { billing: ["manage"] },
	},
	{
		id: "settings",
		to: "/settings",
		label: "Settings",
		description: "Account and preferences",
		keywords: ["settings", "account", "preferences", "profile"],
		icon: Settings,
	},
];

// The developer docs live on their own site, so this entry opens them in a new tab.
const DOCS_ENTRY = {
	id: "docs",
	keywords: ["docs", "documentation", "guide", "reference", "api", "mcp", "help"],
	icon: BookOpen,
} as const;

//
// Bar
//

// "⌘K" on mac, "Ctrl+K" on win/linux. The platform check runs once.
const SHORTCUT_HINT = formatForDisplay("Mod+K");

export function SiteSearch() {
	const navigate = useNavigate();
	const { can } = useAuth();
	const t = useTranslations("common.search");
	const locale = useLocale();

	// A page the member cannot reach never shows up. The localized copy is
	// overlaid so ranking matches the language the user sees (and types).
	// Keywords stay as English synonym bonuses.
	const pages = useMemo(
		() => [
			...SEARCH_PAGES.filter((e) => !e.permission || can(e.permission)).map((e) => ({
				...e,
				label: t(`pages.${e.id}.label`),
				description: t(`pages.${e.id}.description`),
			})),
			{
				...DOCS_ENTRY,
				to: docsLink("home", locale),
				label: t("pages.docs.label"),
				description: t("pages.docs.description"),
			},
		],
		[can, t, locale],
	);

	const search = useSiteSearch({
		pages,
		navigate: (to) => {
			const page = SEARCH_PAGES.find((e) => e.to === to);
			if (page) void navigate({ to: page.to });
			else window.open(to, "_blank", "noopener,noreferrer");
		},
	});

	// Cmd+K (mac) / Ctrl+K (win/linux) focuses the search bar. preventDefault
	// stops the browser's own Ctrl+K omnibox action from stealing it.
	useHotkey("Mod+K", search.focus, { preventDefault: true });

	return (
		<SearchBar
			search={search}
			placeholder={t("placeholder")}
			noResults={t("noResults")}
			shortcutHint={SHORTCUT_HINT}
		/>
	);
}
